"use client";

import { useEffect, useRef, useState } from "react";
import { formatRelativeTime, useTranslation } from "../i18n";
import { type StoredThread, VISIBLE_THREAD_LIMIT } from "../lib/thread-store";
import { cn } from "../lib/utils";
import { PARTS } from "../parts";
import { NewThreadButton } from "./new-thread-button";

interface ThreadMenuProps {
	threads: StoredThread[];
	activeThreadId?: string;
	onNewThread: () => void;
	onSelectThread: (threadId: string) => void;
	onDeleteThread: (threadId: string) => void;
}

function HistoryIcon({ label }: { label: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="16"
			height="16"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			role="img"
			aria-label={label}
		>
			<title>{label}</title>
			<path d="M3 12a9 9 0 1 0 3-6.7" />
			<polyline points="3 4 3 9 8 9" />
			<line x1="12" y1="7" x2="12" y2="12" />
			<line x1="12" y1="12" x2="15" y2="14" />
		</svg>
	);
}

function TrashIcon({ label }: { label: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			role="img"
			aria-label={label}
		>
			<title>{label}</title>
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
			<path d="M10 11v6" />
			<path d="M14 11v6" />
		</svg>
	);
}

export function ThreadMenu({
	threads,
	activeThreadId,
	onNewThread,
	onSelectThread,
	onDeleteThread,
}: ThreadMenuProps) {
	const { locale, t } = useTranslation();
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) {
			return;
		}
		const handler = (event: MouseEvent) => {
			const container = containerRef.current;
			if (!container) {
				return;
			}
			// In the embed we render inside a shadow root, so events that
			// bubble to `document` are retargeted to the shadow host and
			// `event.target` no longer points at the actual click target.
			// `composedPath()` reports the true path through the shadow
			// tree, which is what we need to distinguish clicks inside
			// the menu from genuine outside-clicks.
			if (event.composedPath().includes(container)) {
				return;
			}
			setOpen(false);
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, [open]);

	const visible = threads.slice(0, VISIBLE_THREAD_LIMIT);
	const hidden = Math.max(0, threads.length - VISIBLE_THREAD_LIMIT);

	return (
		<div
			ref={containerRef}
			className="ww:relative ww:flex ww:items-center ww:gap-1"
		>
			<NewThreadButton onClick={onNewThread} />
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				title={t.threadMenu.threadHistory}
				aria-label={t.threadMenu.threadHistory}
				aria-expanded={open}
				data-state={open ? "open" : "closed"}
				className={cn(
					PARTS.menuButton,
					"ww:p-1.5 ww:rounded-md ww:text-muted-foreground hover:ww:text-foreground hover:ww:bg-foreground/5 ww:transition-colors ww:cursor-pointer",
				)}
			>
				<HistoryIcon label={t.threadMenu.threadHistory} />
			</button>
			{open && (
				<div
					role="menu"
					className={cn(
						PARTS.menu,
						"ww:absolute ww:right-0 ww:top-full ww:mt-1 ww:w-72 ww:max-h-80 ww:overflow-y-auto ww:rounded-lg ww:border ww:border-border ww:bg-background ww:shadow-lg ww:z-50",
					)}
				>
					{visible.length === 0 && (
						<div className="ww:px-3 ww:py-3 ww:text-xs ww:text-muted-foreground">
							{t.threadMenu.noPreviousChats}
						</div>
					)}
					{visible.map((thread) => {
						const isActive = thread.threadId === activeThreadId;
						const updatedAtMs = Date.parse(thread.updatedAt);
						return (
							<div
								key={thread.threadId}
								className={cn(
									PARTS.menuItem,
									"ww:flex ww:items-center ww:gap-2 ww:px-3 ww:py-2 ww:text-xs hover:ww:bg-foreground/5",
									isActive && "ww:bg-foreground/5",
								)}
							>
								<button
									type="button"
									data-action="open-thread"
									onClick={() => {
										onSelectThread(thread.threadId);
										setOpen(false);
									}}
									className="ww:flex-1 ww:min-w-0 ww:text-left ww:cursor-pointer"
								>
									<div className="ww:truncate ww:text-foreground ww:font-medium">
										{thread.title}
									</div>
									<div className="ww:text-[10px] ww:text-muted-foreground">
										{Number.isNaN(updatedAtMs)
											? ""
											: formatRelativeTime(updatedAtMs, locale)}
									</div>
								</button>
								<button
									type="button"
									data-action="delete"
									onClick={(e) => {
										e.stopPropagation();
										onDeleteThread(thread.threadId);
									}}
									title={t.threadMenu.deleteThread}
									aria-label={t.threadMenu.deleteThread}
									className="ww:p-1 ww:rounded ww:text-muted-foreground hover:ww:text-foreground hover:ww:bg-foreground/10 ww:transition-colors ww:cursor-pointer"
								>
									<TrashIcon label={t.threadMenu.deleteThread} />
								</button>
							</div>
						);
					})}
					{hidden > 0 && (
						<div className="ww:px-3 ww:py-2 ww:text-[10px] ww:text-muted-foreground ww:border-t ww:border-border">
							{t.threadMenu.hiddenThreads(hidden)}
						</div>
					)}
				</div>
			)}
		</div>
	);
}
